import React from "react";
import PropTypes from "prop-types";

/**
 * @param {string} raw
 * @returns {"high"|"mid"|"low"|null}
 */
export function normalizeLeadScoreTier(raw) {
    const s = String(raw || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
    if (!s) {
        return null;
    }
    // Backward-compatible aliases from older API values.
    if (s === "high" || s === "core") {
        return "high";
    }
    if (s === "mid" || s === "medium" || s === "moderate") {
        return "mid";
    }
    if (s === "low" || s === "peripheral" || s === "periferal" || s === "perifetal") {
        return "low";
    }
    return null;
}

const CANONICAL_SCORE_LABELS = {
    high: "High",
    mid: "Moderate",
    low: "Low"
};

/**
 * @param {string} [score]
 * @param {{ displayCanonical?: boolean }} [options]
 * @returns {{ tier: string, className: string, label: string }}
 */
export function getLeadScoreBadgeProps(score, options = {}) {
    const raw = String(score || "").trim();
    if (!raw) {
        return {
            tier: "unknown",
            className: "lead-score-badge lead-score-badge--unknown",
            label: "Unavailable"
        };
    }
    const tier = normalizeLeadScoreTier(raw);
    const tierKey = tier || "unknown";
    const label =
        options.displayCanonical && tier && CANONICAL_SCORE_LABELS[tier]
            ? CANONICAL_SCORE_LABELS[tier]
            : raw;
    return {
        tier: tierKey,
        className: `lead-score-badge lead-score-badge--${tierKey}`,
        label
    };
}

export function LeadScoreBadge({ score, className = "", size = "md", displayCanonical = false }) {
    const { className: tierClass, label } = getLeadScoreBadgeProps(score, { displayCanonical });
    const sizeClass =
        size === "lg" ? "lead-score-badge--lg" : size === "sm" ? "lead-score-badge--sm" : "";
    const title = label || undefined;
    return (
        <span
            className={[tierClass, sizeClass, className].filter(Boolean).join(" ")}
            title={title}
            aria-label={label ? `Lead score: ${label}` : "Lead score unavailable"}
        >
            {label}
        </span>
    );
}

LeadScoreBadge.propTypes = {
    score: PropTypes.string,
    className: PropTypes.string,
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    displayCanonical: PropTypes.bool
};
